/**
 * R6 §8.3 — the chrome frame. ⭐ **R9: TWO SLOTS, NOT THREE.**
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │   <SectionHeader> — brand · ml-auto · theme · me           │  ← NOT navigation
 *   ├──────────────┬─────────────────────────────────────────────┤
 *   │  <NavRail>   │           <main id="main">{children}</main>  │  ← every destination
 *   └──────────────┴─────────────────────────────────────────────┘
 *
 * ⚠️ The header used to carry a section strip and an app chip and the rail showed ONE section. Both
 * header controls are deleted and the rail carries everything (R9 N-1/N-2/N-5), so this component's
 * `sections` / `activeSectionId` / `appChip` props are gone with them — there is no longer any such
 * thing as "the active section" in the chrome.
 *
 * Ported from `site-admin-ui/src/shell/SectionLayout.tsx` (D-16), including `GrantsErrorBanner`
 * VERBATIM, plus the skip link and the focusable `<main>` (WS-E's a11y bar).
 */
import { useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { BrandFollowsPolarity } from '../branding/BrandFollowsPolarity';
import { PolarityProvider } from '../theme/PolarityContext';
import { SectionHeader, type ShellSession } from './SectionHeader';
import { NavRail, type RailItem, type RailScope } from './NavRail';
import type { GrantsError } from './ZeroAccessPanel';
import { ElevationNotice } from './ElevationNotice';

/**
 * Which error a dismissal was FOR — `kind` + `subject` + `message`, i.e. every field of
 * `GrantsError`. Its FULL identity, not a summary of it.
 *
 * ⛔ `subject` IS PART OF THE IDENTITY, and it had to become part of it the moment the banner
 * started READING it (below). Two errors sharing a `kind` and a `message` but differing in
 * `subject` now render DIFFERENT SENTENCES, so treating them as one identity would let a dismissal
 * of the grants banner silence the apps banner — a different failure, with a different remedy,
 * silently suppressed. That is precisely the "dismiss the channel, not the error" defect this
 * function exists to prevent, and reading a field without adding it here would have re-created it.
 *
 * A plain space separator stays unambiguous because BOTH discriminants are closed enums whose
 * values contain no spaces (`'fetch' | 'truncated'`, `'access' | 'apps' | undefined`), so no field
 * triple can produce the same string as a different one. ⚠️ If either ever gains a value with a
 * space, this needs a separator that cannot occur in it.
 */
function errorIdentity(error: GrantsError): string {
  return `${error.kind} ${error.subject ?? 'access'} ${error.message}`;
}

/**
 * The sentence for this error. ⛔ TWO AXES, NOT ONE — and they are not interchangeable:
 *   · `subject` selects WHAT FAILED, because the operator's next question is "what broke?";
 *   · `kind` selects the REMEDY, because a failed fetch is worth retrying and a truncated
 *     expansion is not.
 *
 * ⚠️ The `subject` arm exists because a failed `GET /v1/apps` reuses this banner. Without it the
 * banner said *"Some resource-level permissions couldn't be loaded"* on a shell where resource
 * permissions loaded **fine** — naming the wrong failure, and sending the operator to check the
 * wrong thing. `ZeroAccessPanel.tsx:25-36` made exactly this one-axis change for the same reason and
 * says so; this card is the same card one layer out and was simply missed (lead ruling ACK-ws0-3 /
 * G4).
 *
 * ⛔ `subject` is checked FIRST because an app-list failure has one remedy — reload — whatever the
 * `kind`, and there is no such thing as a truncated app list.
 */
function bannerSentence(error: GrantsError): string {
  if (error.subject === 'apps') {
    return 'The list of apps you can reach didn’t load, so the Apps section may be incomplete. Try reloading.';
  }
  return error.kind === 'truncated'
    ? 'Your resource-level permissions are too large to evaluate, so controls for individual apps may appear unavailable. Reloading will not help — ask a platform admin to replace your per-resource grants with a role.'
    : 'Some resource-level permissions couldn’t be loaded, so controls for individual apps may appear unavailable. Try reloading.';
}

/**
 * R2/M4 — the ONLY surface that tells a principal WITH visible sections that their
 * resource-level grants could not be loaded.
 *
 * Why it has to exist. `buildPermissionSnapshot` collapses an unusable `resourceGrants` to `[]`,
 * which is correct for permission CHECKS (fail closed) — but it means every
 * `useHasPermission(p, resourceId)` answers `false`, so resource-gated controls silently vanish.
 * A wrongly-disabled control is indistinguishable from a real denial. Before this banner the
 * zero-access panel was the error's only home, and that panel renders ONLY for a principal with
 * no visible section — so the release's core invariant held exclusively for the population that
 * has nowhere to go.
 *
 * A BANNER, not a modal or a blanked page: the operator can still do everything their
 * site-level roles allow. They just now know why a button is missing.
 *
 * The two kinds are worded differently on purpose. Their REMEDIES differ — a failed fetch is
 * worth retrying, a truncated expansion is not — which is the whole reason `GrantsError` is
 * discriminated rather than a bare string. Collapsing them here would put the
 * two-remedies-one-message defect back, one layer up.
 */
function GrantsErrorBanner({ error }: { readonly error: GrantsError }): React.JSX.Element | null {
  /* ⛔ The dismissal is keyed to the error it dismissed, NOT a bare boolean. It was
     `useState(false)` — a flag attached to nothing. This component stays mounted for the whole
     session (it is the shell chrome), so once dismissed it stayed dismissed for every LATER,
     DIFFERENT grants error: dismiss the transient "couldn't be loaded, try reloading" banner, and
     when the expansion later comes back TRUNCATED — the kind whose remedy is completely different
     and whose retry is futile — the operator is told nothing at all, while every resource-gated
     control silently disappears. That is the `absent → denied` collapse this banner exists to
     prevent, re-created by its own dismiss button. Dismissing an error must silence THAT error,
     not the channel. Re-showing the SAME error would be nagging, so identity, not a counter or a
     timestamp. */
  const [dismissed, setDismissed] = useState<string | null>(null);
  const identity = errorIdentity(error);
  if (dismissed === identity) return null;
  return (
    <div
      data-testid="grants-error-banner"
      role="status"
      aria-live="polite"
      className="flex items-start gap-3 border-b border-state-warning/30 bg-state-warning/5 px-6 py-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-state-warning" aria-hidden />
      <p className="flex-1 text-xs text-secondary">
        {bannerSentence(error)}{' '}
        <span className="font-mono text-2xs text-muted">{error.message}</span>
      </p>
      <button
        type="button"
        onClick={() => setDismissed(identity)}
        data-testid="grants-error-banner-dismiss"
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 text-muted transition-colors hover:bg-surface-muted hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export interface SectionLayoutProps {
  readonly children: ReactNode;
  /**
   * ⛔ `false` on every non-populated path. Measured on the sibling: an entry-less rail does not
   * degrade to nothing, it degrades to a full-height 240px bordered empty column — the strongest
   * "this is broken, or still loading" signal on the page, which is the exact confusion the
   * zero-access panel exists to remove.
   *
   * ⚠️ **It is `true` on every POPULATED frame, including in app scope** (R9). It used to be `false`
   * whenever the app-scope tab strip was up, because the strip replaced the rail — and that
   * replacement was the owner's actual complaint: inside an app you lost the console.
   */
  readonly showRail?: boolean;
  /** Every visible GLOBAL destination, all sections. `NavRail` groups them by `SECTION_ORDER`. */
  readonly railItems?: readonly RailItem[];
  /**
   * The record currently in scope and its destinations — the rail's scoped SIBLING region (N-5), or
   * `null` on every unscoped screen.
   *
   * ⭐ **ONE DESCRIPTOR, REPLACING `appScopedItems` + `railAppScope` + `scopePicker`** (R9-13). Those
   * three were only ever meaningful together and only ever described an APP; the descriptor is
   * scope-shaped, so the data plane is an adopter rather than an edit and the tenant scope after it
   * will be too. ⛔ Passed separately from `railItems` for the original reason, unchanged: folding a
   * scope's destinations into the Apps group is the nesting N-5 refused.
   */
  readonly railScope?: RailScope | null;
  readonly showSingleAppNote?: boolean;
  /**
   * Non-null renders the dismissible banner above `<main>`.
   *
   * Defaults to `null`, which is what keeps the ZERO-ACCESS caller from rendering the error
   * twice: that path passes the same error to `ZeroAccessPanel`, whose whole job is to explain it
   * in full.
   */
  readonly grantsError?: GrantsError | null;
  readonly session?: ShellSession | null;
}

export function SectionLayout({
  children,
  showRail = true,
  railItems = [],
  railScope = null,
  showSingleAppNote = false,
  grantsError = null,
  session = null,
}: SectionLayoutProps): React.JSX.Element {
  /*
   * ⚠️ **THE RAIL'S ACCESSIBLE NAME IS NO LONGER COMPUTED HERE, AND THAT IS THE POINT.** It used to be
   * the ACTIVE SECTION's label (`"Platform navigation"`), which was correct while the rail showed one
   * section. The rail now shows all of them, so its global landmark is statically `"Console"` and its
   * scoped landmark is named by its own visible label via `aria-labelledby` — one source per name,
   * neither of them this component's business.
   */
  return (
    /*
      ⭐ WS-E / D-18 — POLARITY IS SCOPED TO THE CHROME, and this is the deliberate choice rather
      than the obvious one.

      The obvious placement is the composition root (`CpAdminApp`). It is rejected because the
      provider's only two consumers are both chrome — the header's `PolarityToggle` and
      `BrandFollowsPolarity` — and because at the root the wiring is only assertable by reading the
      source: nothing in this package renders `CpAdminAppView`, and branch 4 (the only branch with a
      header) needs the whole auth + MSW stack to reach. Here, the wiring is proved by rendering the
      chrome, which several suites already do. ⛔ That matters more than tidiness, because
      `usePolarityContext` has a DEFAULT value with a no-op `setPolarity`: a toggle outside a provider
      renders perfectly, focuses, announces correctly and does NOTHING when pressed. Only a DOM test
      that presses it can tell the two apart, and `header-polarity.test.tsx` is that test.

      ⚠️ WHAT THIS PLACEMENT GIVES UP, stated rather than discovered: the login and change-password
      screens sit ABOVE this component, so they do not get a live subscription to the OS theme. They
      are still correctly themed — `main.tsx` calls `applyPolarityBeforePaint()` before first paint —
      so the only loss is an OS theme change taking effect while an operator sits on the login form,
      un-refreshed. They also have no toggle to press. If a toggle is ever added to those screens,
      this provider must move up to the composition root and the wiring test must move with it.
    */
    <PolarityProvider>
      {/* Re-tunes the brand's accent pair whenever the resolved polarity changes. Inside
          `CpBrandingProvider` (which is above this component) and inside `PolarityProvider`, which
          is the only position where it can read both. */}
      <BrandFollowsPolarity />
      <div className="flex h-screen flex-col bg-surface">
      {/*
        The skip link (WS-E's a11y bar). ⛔ It must be the FIRST focusable thing in the document
        and it must become VISIBLE on focus — a permanently-hidden skip link is the classic
        decoration: it satisfies an automated checker and helps nobody, because a sighted keyboard
        user cannot tell they have focused it. `sr-only` + `focus:not-sr-only` is the pattern that
        does both.
      */}
      <a
        href="#main"
        data-testid="skip-to-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-surface-elevated focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary focus:outline-2 focus:outline-offset-2 focus:outline-focus-ring"
      >
        Skip to main content
      </a>
      <SectionHeader session={session} />
      {grantsError === null ? null : <GrantsErrorBanner error={grantsError} />}
      {/* The elevation notice slot (K8): empty unless an elevation speaks; absent with no active-role provider. */}
      <ElevationNotice />
      <div className="flex flex-1 overflow-hidden">
        {showRail ? (
          <NavRail
            globalItems={railItems}
            scope={railScope}
            showSingleAppNote={showSingleAppNote}
          />
        ) : null}
        {/*
          ⛔ `tabIndex={-1}` is what makes the skip link actually work. Without it the browser
          moves the URL fragment but NOT keyboard focus in several engines, so the next Tab
          continues from the skip link rather than from the content — the link appears to do
          nothing, which is worse than not having one.

          ⛔⛔ `relative` IS NOT COSMETIC, and nothing in this file positions against it. It is the
          BACKSTOP for the document-scroll defect documented in `PaneScrollLayout.tsx`: Tailwind's
          `sr-only` is `position: absolute`, and an absolutely positioned element with no positioned
          ancestor takes the INITIAL containing block — the document — so it is laid out at document
          coordinates and stretches `<html>` past the viewport, escaping every `overflow` between it
          and the root. Measured: one `sr-only` span in `TenantsPane` put
          `document.documentElement.scrollHeight` at 1111 against a `clientHeight` of 813, and
          `window.scrollTo(0, 5000)` really moved `scrollY` to 298. The pane's scroll region carries
          its own `relative` for the content INSIDE it, the chrome slot included. This covers
          everything else under `<main>`: the `AppLoader` fallback and any screen that is not a pane.

          ⚠️ It changes nothing else: `relative` with `z-index: auto` creates no stacking context, and
          `position: fixed` (every dialog) and `sticky` (table headers) are unaffected by a merely
          relative ancestor.
        */}
        <main
          id="main"
          tabIndex={-1}
          className="relative flex-1 overflow-y-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          data-testid="section-content"
        >
          {children}
        </main>
        </div>
      </div>
    </PolarityProvider>
  );
}
